import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useUserStore } from '../stores/useUserStore';
import { useTranslation } from '../hooks/useTranslation';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Search,
  Filter,
  Calendar,
  DollarSign,
  Tag,
  FileText,
  Edit,
  Eye,
  Trash2,
  Download,
  Upload,
  BarChart3,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';
import { nekoPos } from 'public-contracts';
import CostService from '../services/costService';
import { useListFetch } from '../hooks/useListFetch';
import { ListFetchStates } from '../components/common/ListFetchStates';
import { QuickDateRange } from '../components/common/QuickDateRange';
import { DateRangePreset } from '../utils/dateRangeUtils';

type Cost = nekoPos.Cost;
type CostCategory = nekoPos.CostCategory;

interface CostWithCategory {
  id: string;
  description: string;
  amount: number;
  categoryId: string;
  method?: string;
  date: Date;
  receipt?: string;
  notes?: string;
  category?: nekoPos.CostCategory;
}

const ModernCostList: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { user, fetchUser } = useUserStore();
  const { formatCurrency } = useCurrencyFormat();
  const costService = useMemo(() => new CostService(), []);

  // Pagination state
  const { data, isLoading, isError, fetchData } = useListFetch<{
    costs: CostWithCategory[];
    total: number;
  }>();
  const [costs, setCosts] = useState<CostWithCategory[]>([]);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20, // Items per page
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasLoadedOnceRef = useRef(false);

  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('custom');
  const [viewMode, setViewMode] = useState<'table' | 'cards' | 'auto'>('auto');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [error, setError] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [costToDelete, setCostToDelete] = useState<string | null>(null);
  const [sort, setSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({
    field: 'date',
    order: 'DESC',
  });
  const [totalAmount, setTotalAmount] = useState<number>(0);

  // Load categories for filter dropdown
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await costService.getCostCategories({
          page: 1,
          perPage: 1000, // Fetch all categories for dropdown
        });
        const categoriesArray = Array.isArray(categoriesData)
          ? categoriesData
          : [];
        setCategories(categoriesArray);
        console.log(
          `✅ Loaded ${categoriesArray.length} categories (total: ${
            (categoriesData as any).__total || categoriesArray.length
          })`
        );
      } catch (err) {
        console.error('Failed to load cost categories:', err);
      }
    };
    loadCategories();
  }, [costService]);

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search changes
      if (searchTerm !== debouncedSearchTerm) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Build category tree structure
  const categoryTree = useMemo(() => {
    const categoryMap = new Map<string, CostCategory & { children: CostCategory[] }>();
    const rootCategories: (CostCategory & { children: CostCategory[] })[] = [];

    // First pass: create map with children arrays
    categories.forEach((category) => {
      categoryMap.set(category.id, { ...category, children: [] });
    });

    // Second pass: build tree structure
    categories.forEach((category) => {
      const categoryWithChildren = categoryMap.get(category.id)!;
      if (category.parent && categoryMap.has(category.parent)) {
        const parent = categoryMap.get(category.parent)!;
        parent.children.push(categoryWithChildren);
      } else {
        rootCategories.push(categoryWithChildren);
      }
    });

    // Sort by name
    const sortCategories = (
      cats: (CostCategory & { children: CostCategory[] })[]
    ) => {
      cats.sort((a, b) => a.name.localeCompare(b.name));
      cats.forEach((cat) => {
        if (cat.children.length > 0) {
          sortCategories(cat.children as (CostCategory & { children: CostCategory[] })[]);
        }
      });
    };

    sortCategories(rootCategories);
    return rootCategories;
  }, [categories]);

  // Get all descendant category IDs (including the category itself)
  const getAllDescendantIds = useCallback((categoryId: string): string[] => {
    const result: string[] = [categoryId];
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return result;

    // Find all children recursively
    const findChildren = (parentId: string) => {
      categories.forEach((cat) => {
        if (cat.parent === parentId) {
          result.push(cat.id);
          findChildren(cat.id);
        }
      });
    };

    findChildren(categoryId);
    return result;
  }, [categories]);

  // Handle category selection (with parent-child logic)
  const handleCategoryToggle = useCallback((categoryId: string, checked: boolean) => {
    setSelectedCategories((prev) => {
      const newSet = new Set(prev);
      const descendantIds = getAllDescendantIds(categoryId);

      if (checked) {
        // Add category and all descendants
        descendantIds.forEach((id) => newSet.add(id));
      } else {
        // Remove category and all descendants
        descendantIds.forEach((id) => newSet.delete(id));
      }

      return newSet;
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [getAllDescendantIds]);

  const handleRangeChange = (from: string, to: string, preset: DateRangePreset) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      setDateFrom(from);
      setDateTo(to);
    }
  };

  // Check if category is selected (including if any ancestor is selected)
  const isCategorySelected = useCallback((categoryId: string): boolean => {
    return selectedCategories.has(categoryId);
  }, [selectedCategories]);

  // Check if category is partially selected (some but not all children selected)
  const isCategoryPartiallySelected = useCallback((category: CostCategory & { children: CostCategory[] }): boolean => {
    if (category.children.length === 0) return false;
    const selectedChildren = category.children.filter((child) => selectedCategories.has(child.id));
    return selectedChildren.length > 0 && selectedChildren.length < category.children.length;
  }, [selectedCategories]);

  // Build filter object for API
  const buildFilterObject = useCallback(() => {
    const filterObj: any = {};

    if (selectedCategories.size > 0) {
      // Use $in operator to filter by multiple category IDs
      filterObj.categoryId = { $in: Array.from(selectedCategories) };
    }

    if (dateFrom) {
      filterObj.date = { $gte: new Date(dateFrom).toISOString() };
    }

    if (dateTo) {
      if (filterObj.date) {
        filterObj.date.$lte = new Date(dateTo).toISOString();
      } else {
        filterObj.date = { $lte: new Date(dateTo).toISOString() };
      }
    }

    return Object.keys(filterObj).length > 0 ? filterObj : undefined;
  }, [selectedCategories, dateFrom, dateTo]);

  // Handle sorting
  const handleSort = useCallback(
    (field: string) => {
      const newOrder =
        sort.field === field && sort.order === 'ASC' ? 'DESC' : 'ASC';
      setSort({ field, order: newOrder });
      // Reset to page 1 when sorting changes
      setPagination((prev) => ({ ...prev, page: 1 }));
    },
    [sort.field, sort.order]
  );

  // Get sort icon for a field
  const getSortIcon = (field: string) => {
    if (sort.field !== field) return null;
    return sort.order === 'ASC' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  // Fetch total amount separately (respecting filters/search)
  const fetchTotalAmount = useCallback(async () => {
    try {
      const currentFilter = buildFilterObject();
      const currentSearch = debouncedSearchTerm;
      
      const total = await costService.getTotalAmount({
        filter: currentFilter,
        q: currentSearch || undefined,
      });
      
      setTotalAmount(total);
    } catch (error) {
      console.error('Error fetching total amount:', error);
      // Keep previous value on error
    }
  }, [debouncedSearchTerm, buildFilterObject, costService]);

  // Fetch costs - follows ProductList pattern with retry logic
  const handleFetchCosts = useCallback(
    async (retryCount = 0) => {
      const maxRetries = 3;
      const currentPage = pagination.page;
      const currentPerPage = pagination.perPage;
      const currentSearch = debouncedSearchTerm;
      const currentFilter = buildFilterObject();

      try {
        const response = await fetchData(async () => {
          console.log(
            `🔄 Loading costs page ${currentPage} (${currentPerPage} per page)...`,
            { search: currentSearch, filter: currentFilter }
          );

          const result = await costService.getCosts({
            page: currentPage,
            size: currentPerPage,
            q: currentSearch || undefined,
            filter: currentFilter,
            sort: [sort.field, sort.order],
          });

          const costsArray = Array.isArray(result) ? result : [];
          const total = (result as any).__total ?? 0;

          console.log(
            `✅ Loaded ${costsArray.length} costs (page ${currentPage}, total: ${total})`
          );

          // Combine costs with their categories and ensure amounts are numbers
          const costsWithCategories: CostWithCategory[] = costsArray.map(
            (cost: Cost) => ({
              ...cost,
              amount:
                typeof cost.amount === 'string'
                  ? parseFloat((cost.amount as string).replace(/,/g, '')) || 0
                  : Number(cost.amount) || 0,
              category: categories.find(
                (cat: CostCategory) => cat.id === cost.categoryId
              ),
            })
          );

          return {
            costs: costsWithCategories,
            total,
          };
        });

        if (response) {
          setCosts(response.costs);
          setPagination((prev) => ({ ...prev, total: response.total }));
          hasLoadedOnceRef.current = true;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load costs';
        console.error(
          `❌ Failed to load costs (attempt ${retryCount + 1}/${
            maxRetries + 1
          }):`,
          err
        );

        // Retry on network errors
        if (
          retryCount < maxRetries &&
          (errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('ERR_CONNECTION_REFUSED') ||
            errorMessage.includes('NetworkError'))
        ) {
          console.log(`🔄 Retrying in ${(retryCount + 1) * 1000}ms...`);
          await new Promise((resolve) =>
            setTimeout(resolve, (retryCount + 1) * 1000)
          );
          return handleFetchCosts(retryCount + 1);
        }

        setCosts([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    },
    [
      pagination.page,
      pagination.perPage,
      debouncedSearchTerm,
      buildFilterObject,
      fetchData,
      categories,
      sort.field,
      sort.order,
    ]
  );

  // Auto-fetch data when component mounts or dependencies change
  useEffect(() => {
    handleFetchCosts();
  }, [
    pagination.page,
    pagination.perPage,
    debouncedSearchTerm,
    selectedCategories,
    dateFrom,
    dateTo,
    sort.field,
    sort.order,
    handleFetchCosts,
  ]);

  // Fetch total amount when filters/search change (but not on pagination)
  useEffect(() => {
    fetchTotalAmount();
  }, [
    debouncedSearchTerm,
    selectedCategories,
    dateFrom,
    dateTo,
    fetchTotalAmount,
  ]);

  const hasPermission = (action: 'list' | 'create' | 'edit' | 'delete') => {
    if (!user?.roles) return false;
    if (user.roles.includes('sysAdmin')) return true;
    if (user.roles.includes('admin')) return true;
    if (user.roles.includes('staff')) return true;
    if (user.roles.includes('storeManager')) return true;
    if (user.roles.includes('cashier')) {
      return action !== 'delete';
    }
    return false;
  };

  if (!hasPermission('list')) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {translate('pos.common.accessDenied') || 'Access Denied'}
          </p>
          <p className="text-muted-foreground">
            {translate('pos.common.noPermissionCostManagement') ||
              'You do not have permission to view cost management.'}
          </p>
        </div>
      </div>
    );
  }

  const handleSearch = () => {
    // Immediately update debounced search and reset to page 1
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setDebouncedSearchTerm(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(pagination.total / pagination.perPage)
    ) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const formatDate = (date: Date) => {
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        return '-';
      }
      return new Intl.DateTimeFormat('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(dateObj);
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return '-';
    }
  };

  const handleCreateCost = () => {
    navigate('/costs/create');
  };

  const handleEditCost = (id: string) => {
    navigate(`/costs/${id}/edit`);
  };

  const handleViewCost = (id: string) => {
    navigate(`/costs/${id}`);
  };

  const handleDeleteCost = (id: string) => {
    setCostToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!costToDelete) return;

    try {
      await costService.deleteCost(costToDelete);
      setCosts(costs.filter((cost) => cost.id !== costToDelete));
      setDeleteDialogOpen(false);
      setCostToDelete(null);
    } catch (error) {
      console.error('Error deleting cost:', error);
      setError(translate('pos.common.failedToDeleteCost'));
      setDeleteDialogOpen(false);
      setCostToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {translate('pos.costs.name', 'Cost Management')}
          </h1>
          <p className="text-muted-foreground">
            {translate(
              'pos.costs.subtitle',
              'Track and manage business expenses'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasPermission('create') && (
            <Button onClick={handleCreateCost}>
              <Plus className="h-4 w-4 mr-2" />
              {translate('pos.common.action.create')}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.costs.stats.total_costs', 'Total Costs')}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <span data-sensitive-field="amount">
                {formatCurrency(totalAmount)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {pagination.total}{' '}
              {translate('pos.costs.stats.entries', 'entries')}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.costs.stats.this_month', 'This Month')}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <span data-sensitive-field="amount">
                {formatCurrency(
                  costs
                    .filter((cost) => {
                      const now = new Date();
                      const costDate = new Date(cost.date);
                      return (
                        costDate.getMonth() === now.getMonth() &&
                        costDate.getFullYear() === now.getFullYear()
                      );
                    })
                    .reduce((sum, cost) => {
                      const amount =
                        typeof cost.amount === 'number'
                          ? cost.amount
                          : Number(cost.amount) || 0;
                      return sum + amount;
                    }, 0)
                )}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.costs.stats.current_month_expenses',
                'Current month expenses'
              )}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.costs.stats.categories', 'Categories')}
            </CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.costs.stats.active_categories',
                'Active categories'
              )}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.costs.stats.average_cost', 'Average Cost')}
            </CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <span data-sensitive-field="amount">
                {costs.length > 0
                  ? formatCurrency(totalAmount / costs.length)
                  : formatCurrency(0)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {translate('pos.costs.stats.per_entry', 'Per entry')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters - Always visible */}
      <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    placeholder={translate(
                      'pos.costs.search_placeholder',
                      'Search costs...'
                    )}
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const wasFocused =
                        document.activeElement === searchInputRef.current;
                      setSearchTerm(e.target.value);
                      // Restore focus if it was focused before
                      if (wasFocused && searchInputRef.current) {
                        setTimeout(() => {
                          searchInputRef.current?.focus();
                        }, 0);
                      }
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleSearch}>
                  {translate('common.search', 'Search')}
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="min-w-[200px] justify-between"
                    >
                      <span>
                        {selectedCategories.size === 0
                          ? translate('pos.costs.all_categories', 'All Categories')
                          : selectedCategories.size === 1
                          ? categories.find((c) => selectedCategories.has(c.id))?.name ||
                            translate('pos.costs.categories_selected', '1 category')
                          : translate(
                              'pos.costs.categories_selected',
                              `${selectedCategories.size} categories`
                            )}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="max-h-[400px] overflow-y-auto p-4">
                      <div className="mb-2 font-semibold">
                        {translate('pos.costs.select_categories', 'Select Categories')}
                      </div>
                      <div className="space-y-1">
                        {categoryTree.length === 0 ? (
                          <div className="text-sm text-muted-foreground py-2">
                            {translate('pos.costs.no_categories', 'No categories available')}
                          </div>
                        ) : (
                          categoryTree.map((category) => (
                            <CategoryTreeNode
                              key={category.id}
                              category={category}
                              level={0}
                              selectedCategories={selectedCategories}
                              expandedCategories={expandedCategories}
                              onToggleCategory={handleCategoryToggle}
                              onToggleExpand={(id) => {
                                setExpandedCategories((prev) => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(id)) {
                                    newSet.delete(id);
                                  } else {
                                    newSet.add(id);
                                  }
                                  return newSet;
                                });
                              }}
                              isSelected={isCategorySelected}
                              isPartiallySelected={isCategoryPartiallySelected}
                            />
                          ))
                        )}
                      </div>
                      {selectedCategories.size > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full"
                            onClick={() => {
                              setSelectedCategories(new Set());
                              setPagination((prev) => ({ ...prev, page: 1 }));
                            }}
                          >
                            {translate('pos.costs.clear_selection', 'Clear Selection')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilterModal(true)}
                  className="border-border text-foreground hover:bg-muted"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {translate('pos.common.action.filter', 'Filter')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategories(new Set());
                    setDateFrom('');
                    setDateTo('');
                    setSelectedPreset('custom');
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  className="border-border text-foreground hover:bg-muted"
                >
                  {translate('pos.common.action.clear', 'Clear')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

      {/* Loading and Error States - Only show in results area */}
      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <BarChart3 className="h-6 w-6 text-primary animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold">
                  {translate('common.loading', 'Loading costs...')}
                </h3>
                <p className="text-muted-foreground">
                  {translate('common.fetching_data', 'Fetching data...')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {isError && !isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <FileText className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold">
                  {translate(
                    'pos.costs.error_loading',
                    'Failed to load costs'
                  )}
                </h3>
                <p className="text-muted-foreground">
                  {translate(
                    'common.try_again_message',
                    'Please try again or contact support if the problem persists.'
                  )}
                </p>
              </div>
              <Button onClick={handleFetchCosts} variant="outline">
                {translate('common.try_again', 'Try Again')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {!isLoading && !isError && (
        <>
          {costs.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {translate('pos.costs.no_costs_found', 'No costs found')}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {translate(
                    'pos.costs.try_adjusting_filters',
                    'Try adjusting your search or filter criteria'
                  )}
                </p>
                {hasPermission('create') && (
                  <Button onClick={handleCreateCost}>
                    <Plus className="h-4 w-4 mr-2" />
                    {translate('pos.costs.add_first_cost', 'Add First Cost')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Table View */}
              <div
                className={`${
                  viewMode === 'auto'
                    ? 'lg:block'
                    : viewMode === 'table'
                    ? 'block'
                    : 'hidden'
                }`}
              >
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-4 font-medium">
                              {translate(
                                'pos.costs.fields.description',
                                'Description'
                              )}
                            </th>
                            <th className="text-left p-4 font-medium">
                              {translate('pos.costs.fields.amount', 'Amount')}
                            </th>
                            <th className="text-left p-4 font-medium">
                              {translate(
                                'pos.costs.fields.category',
                                'Category'
                              )}
                            </th>
                            <th className="text-left p-4 font-medium">
                              <button
                                onClick={() => handleSort('date')}
                                className="flex items-center gap-1 hover:text-foreground"
                              >
                                {translate('pos.costs.fields.date', 'Date')}
                                {getSortIcon('date')}
                              </button>
                            </th>
                            <th className="text-left p-4 font-medium">
                              {translate('pos.costs.fields.method', 'Method')}
                            </th>
                            <th className="text-left p-4 font-medium">
                              {translate('common.actions', 'Actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {costs.map((cost) => (
                            <tr
                              key={cost.id}
                              className="border-b hover:bg-muted/50"
                            >
                              <td className="p-4">
                                <div>
                                  <div className="font-medium">
                                    {cost.description}
                                  </div>
                                  {cost.notes && (
                                    <div className="text-sm text-muted-foreground">
                                      {cost.notes}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="p-4 font-medium text-red-600">
                                <span data-sensitive-field="amount">
                                  {formatCurrency(cost.amount)}
                                </span>
                              </td>
                              <td className="p-4">
                                <Badge variant="secondary">
                                  {cost.category?.name ||
                                    translate('pos.costs.unknown', 'Unknown')}
                                </Badge>
                              </td>
                              <td className="p-4 text-sm text-muted-foreground">
                                {formatDate(cost.date)}
                              </td>
                              <td className="p-4 text-sm text-muted-foreground">
                                {cost.method || '-'}
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleViewCost(cost.id)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {hasPermission('edit') && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleEditCost(cost.id)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {hasPermission('delete') && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteCost(cost.id)}
                                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Card View */}
              <div
                className={`${
                  viewMode === 'auto'
                    ? 'lg:hidden'
                    : viewMode === 'cards'
                    ? 'block'
                    : 'hidden'
                }`}
              >
                <div className="grid gap-4">
                  {costs.map((cost) => (
                    <Card key={cost.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold">
                              {cost.description}
                            </h3>
                            {cost.notes && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {cost.notes}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-red-600 text-lg">
                              <span data-sensitive-field="amount">
                                {formatCurrency(cost.amount)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                          <div className="flex items-center gap-2">
                            <Tag className="h-4 w-4" />
                            <span>{cost.category?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDate(cost.date)}</span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleViewCost(cost.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {hasPermission('edit') && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => handleEditCost(cost.id)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {hasPermission('delete') && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteCost(cost.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Pagination Controls */}
              {pagination.total > 0 && (
                <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-20">
                  <div className="flex flex-col sm:flex-row items-center justify-between p-4 gap-4">
                    <div className="text-sm text-muted-foreground text-center sm:text-left">
                      {translate(
                        'pos.orders.modern.pagination.showing',
                        {
                          start: (pagination.page - 1) * pagination.perPage + 1,
                          end: Math.min(
                            pagination.page * pagination.perPage,
                            pagination.total
                          ),
                          total: pagination.total,
                        }
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1 || isLoading}
                        className="border-border text-foreground hover:bg-muted disabled:opacity-50"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        {translate(
                          'pos.orders.modern.pagination.previous',
                          'Previous'
                        )}
                      </Button>
                      <div className="text-sm text-muted-foreground px-2">
                        {translate('common.page', 'Page')} {pagination.page} /{' '}
                        {Math.ceil(pagination.total / pagination.perPage)}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={
                          pagination.page * pagination.perPage >=
                            pagination.total || isLoading
                        }
                        className="border-border text-foreground hover:bg-muted disabled:opacity-50"
                      >
                        {translate(
                          'pos.orders.modern.pagination.next',
                          'Next'
                        )}
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-card-foreground">
                {translate('pos.costs.filter_costs', 'Filter Costs')}
              </h3>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowFilterModal(false)}
              >
                ×
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-medium text-foreground mb-2">
                  {translate('pos.costs.select_categories', 'Select Categories')}
                </Label>
                <div className="max-h-[300px] overflow-y-auto border border-border rounded-md p-3">
                  {categoryTree.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-2">
                      {translate('pos.costs.no_categories', 'No categories available')}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {categoryTree.map((category) => (
                        <CategoryTreeNode
                          key={category.id}
                          category={category}
                          level={0}
                          selectedCategories={selectedCategories}
                          expandedCategories={expandedCategories}
                          onToggleCategory={handleCategoryToggle}
                          onToggleExpand={(id) => {
                            setExpandedCategories((prev) => {
                              const newSet = new Set(prev);
                              if (newSet.has(id)) {
                                newSet.delete(id);
                              } else {
                                newSet.add(id);
                              }
                              return newSet;
                            });
                          }}
                          isSelected={isCategorySelected}
                          isPartiallySelected={isCategoryPartiallySelected}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {selectedCategories.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => {
                      setSelectedCategories(new Set());
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                  >
                    {translate('pos.costs.clear_selection', 'Clear Selection')}
                  </Button>
                )}
              </div>

              {/* Date Range Selection */}
              <QuickDateRange
                selectedPreset={selectedPreset}
                onRangeChange={handleRangeChange}
                className="mb-4"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="block text-sm font-medium text-foreground mb-1">
                    {translate('pos.costs.from_date', 'From Date')}
                  </Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setDateFrom(e.target.value);
                      setSelectedPreset('custom');
                    }}
                  />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-foreground mb-1">
                    {translate('pos.costs.to_date', 'To Date')}
                  </Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setDateTo(e.target.value);
                      setSelectedPreset('custom');
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategories(new Set());
                  setDateFrom('');
                  setDateTo('');
                  setPagination((prev) => ({ ...prev, page: 1 }));
                  setShowFilterModal(false);
                }}
                className="flex-1"
              >
                {translate('pos.costs.clear_all', 'Clear All')}
              </Button>
              <Button
                onClick={() => {
                  setPagination((prev) => ({ ...prev, page: 1 }));
                  setShowFilterModal(false);
                }}
                className="flex-1"
              >
                {translate('pos.costs.apply_filters', 'Apply Filters')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate('pos.costs.delete_title', 'Delete Cost')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate(
                'pos.common.confirmDeleteCost',
                'Are you sure you want to delete this cost? This action cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeleteDialogOpen(false);
                setCostToDelete(null);
              }}
            >
              {translate('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {translate('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Category Tree Node Component
interface CategoryTreeNodeProps {
  category: CostCategory & { children: CostCategory[] };
  level: number;
  selectedCategories: Set<string>;
  expandedCategories: Set<string>;
  onToggleCategory: (categoryId: string, checked: boolean) => void;
  onToggleExpand: (categoryId: string) => void;
  isSelected: (categoryId: string) => boolean;
  isPartiallySelected: (category: CostCategory & { children: CostCategory[] }) => boolean;
}

const CategoryTreeNode: React.FC<CategoryTreeNodeProps> = ({
  category,
  level,
  selectedCategories,
  expandedCategories,
  onToggleCategory,
  onToggleExpand,
  isSelected,
  isPartiallySelected,
}) => {
  const hasChildren = category.children.length > 0;
  const isExpanded = expandedCategories.has(category.id);
  const checked = isSelected(category.id);
  const partiallyChecked = isPartiallySelected(category);
  const indent = level * 20;

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1 hover:bg-muted/50 rounded transition-colors"
        style={{ paddingLeft: `${indent}px` }}
      >
        {hasChildren ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0"
            onClick={() => onToggleExpand(category.id)}
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </Button>
        ) : (
          <div className="w-5" />
        )}
        <Checkbox
          id={`category-${category.id}`}
          checked={checked}
          onCheckedChange={(checked) => {
            onToggleCategory(category.id, checked === true);
          }}
          className={partiallyChecked ? 'data-[state=checked]:bg-primary/50' : ''}
        />
        <label
          htmlFor={`category-${category.id}`}
          className="flex-1 text-sm cursor-pointer select-none"
        >
          {category.name}
        </label>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {category.children.map((child) => (
            <CategoryTreeNode
              key={child.id}
              category={child as CostCategory & { children: CostCategory[] }}
              level={level + 1}
              selectedCategories={selectedCategories}
              expandedCategories={expandedCategories}
              onToggleCategory={onToggleCategory}
              onToggleExpand={onToggleExpand}
              isSelected={isSelected}
              isPartiallySelected={isPartiallySelected}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ModernCostList;
